import Vue from 'vue'
import { themes, BASE_THEME_ID, DEFAULT_THEME_ID } from './themes'

/** Deep-merge `override` onto `base`; arrays and primitives are replaced. */
function merge(base, override) {
  if (!override) return base
  const out = { ...base }
  Object.keys(override).forEach((key) => {
    const val = override[key]
    out[key] = val && typeof val === 'object' && !Array.isArray(val)
      ? merge(base[key] || {}, val)
      : val
  })
  return out
}

const STORAGE_KEY = 'optinego_theme'
const SETTINGS_KEY = 'optinego_appSettings'

/** Build-time default (`VUE_APP_THEME`), falling back to the app default. */
function defaultThemeId() {
  const id = process.env.VUE_APP_THEME
  return themes[id] ? id : DEFAULT_THEME_ID
}

/** Whether the persisted session belongs to a staff user (read before the store exists). */
function isStaffSession() {
  try {
    return !!JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}').isStaff
  } catch (e) {
    return false
  }
}

/**
 * Theme chosen by staff: `?theme=` query (previews), then the saved choice.
 * Returns null for non-staff or when nothing valid is set.
 */
function staffThemeId() {
  if (!isStaffSession()) return null
  let id
  try {
    id = new URLSearchParams(window.location.search).get('theme')
  } catch (e) { /* non-browser context */ }
  try {
    id = id || localStorage.getItem(STORAGE_KEY)
  } catch (e) { /* storage unavailable */ }
  return themes[id] ? id : null
}

const defaults = themes[BASE_THEME_ID]

/** The active theme, fully populated (defaults + overrides). Reactive. */
export const theme = Vue.observable(merge(defaults, themes[staffThemeId() || defaultThemeId()]))

/** Themes selectable by the user, for the Settings picker. */
export const themeOptions = Object.keys(themes).map((id) => ({
  id,
  label: themes[id].label || id,
}))

const kebab = (s) => s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)

/** Set the CSS variables for the given mode's tokens on <html>. */
export function applyTokens(dark) {
  const root = document.documentElement
  // Mode-aware logo: themes may ship a `logoDark` for dark backgrounds.
  Vue.set(theme.assets, 'logoCurrent', (dark && theme.assets.logoDark) || theme.assets.logo)
  // Logo for the content pane, which may be coloured independently of the app mode.
  Vue.set(theme.assets, 'logoContent', ((dark || theme.layout.contentDark) && theme.assets.logoDark) || theme.assets.logo)
  const tokens = theme.colors.tokens[dark ? 'dark' : 'light'] || {}
  Object.keys(tokens).forEach((k) => root.style.setProperty(`--app-${kebab(k)}`, tokens[k]))
}

/** Apply everything that lives outside Vue/Vuetify: title, favicon, fonts, CSS vars. */
export function applyTheme(dark = false) {
  const root = document.documentElement
  document.title = theme.brand.appName
  root.setAttribute('data-theme', theme.id)
  root.style.setProperty('--app-font', theme.fonts.family)
  root.style.setProperty('--app-font-heading', theme.fonts.headingFamily || theme.fonts.family)
  root.style.setProperty('--app-font-mono', theme.fonts.mono)
  root.style.setProperty('--app-radius', `${theme.layout.borderRadius}px`)
  // Opt-in custom surfaces (see theme.scss).
  if (theme.colors.tokens.light.surfaceRaised) root.setAttribute('data-surfaces', '')
  else root.removeAttribute('data-surfaces')
  if (theme.colors.tokens.light.textMuted) root.setAttribute('data-typography', '')
  else root.removeAttribute('data-typography')
  applyTokens(dark)

  let fontLink = document.getElementById('theme-font')
  if (theme.fonts.stylesheet) {
    if (!fontLink) {
      fontLink = document.createElement('link')
      fontLink.id = 'theme-font'
      fontLink.rel = 'stylesheet'
      document.head.appendChild(fontLink)
    }
    fontLink.href = theme.fonts.stylesheet
  } else if (fontLink) {
    fontLink.remove()
  }
  if (theme.assets.favicon) {
    let icon = document.querySelector('link[rel="icon"]')
    if (!icon) {
      icon = document.createElement('link')
      icon.rel = 'icon'
      document.head.appendChild(icon)
    }
    icon.href = theme.assets.favicon
  }
}

/**
 * Switch theme at runtime: swap the reactive theme contents, push the palettes
 * into Vuetify, refresh CSS variables and remember the choice.
 *
 * Args:
 *   id: key of a registered theme; unknown ids are ignored.
 *   vuetify: the `$vuetify` instance (palettes live there).
 *   persist: remember the choice (staff picker); false for automatic resets.
 */
export function setTheme(id, vuetify, persist = true) {
  if (!themes[id]) return
  const next = merge(defaults, themes[id])
  Object.keys(next).forEach((k) => Vue.set(theme, k, next[k]))
  vuetify.theme.themes.light = { ...vuetify.theme.themes.light, ...theme.colors.light }
  vuetify.theme.themes.dark = { ...vuetify.theme.themes.dark, ...theme.colors.dark }
  applyTheme(vuetify.theme.dark)
  if (!persist) return
  try {
    localStorage.setItem(STORAGE_KEY, id)
  } catch (e) { /* storage unavailable */ }
}

/**
 * Align the theme with the session: staff get their saved choice, everyone
 * else the default theme (also after a staff logout on a shared browser).
 */
export function syncThemeWithUser(isStaff, vuetify) {
  let id = defaultThemeId()
  if (isStaff) {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (themes[saved]) id = saved
    } catch (e) { /* storage unavailable */ }
  }
  if (id !== theme.id) setTheme(id, vuetify, false)
}

/** Vue plugin: exposes `this.$theme` to every component. */
export default {
  install(Vue) {
    Vue.prototype.$theme = theme
  },
}
